import React from 'react';
import { Link } from 'react-router-dom';
import { FaDownload, FaBook, FaLaptopCode } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

const About: React.FC = () => {
  const { isDarkMode } = useTheme();

  const skills = [
    {
      category: "Programming Languages",
      items: ["Python", "Bash","R", "Java", "C/C++", "MATLAB", "JavaScript", "Bash/Shell", "SQL"]
    },
    {
      category: "Machine Learning & AI",
      items: ["TensorFlow", "PyTorch", "scikit-learn", "OpenCV", "YOLO", "Transformers"]
    },
    {
      category: "Bioinformatics Tools",
      items: ["Snakemake", "Signal Processing", "Genomic Analysis", "Multi-omics Integration"]
    },
    {
      category: "Development & Tools",
      items: ["Simulink", "Benchling", "PyMOL","TCoffee", "MarvinSketch", "SOLIDWORKS", "Docker", "PostgreSQL", "Unity", "Data Analysis", "Arduino"]
    }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-stone-50'}`}>
      {/* Main Content */}
      <main className="pt-20 pb-20">
        <div className="max-w-[1400px] mx-auto px-12">
          
          {/* Hero Section - Image and Bio Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 mb-32">
            {/* Left - Image */}
            <div className="flex items-start justify-center">
              <div className="w-full max-w-[500px]">
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-stone-200'} rounded-[2rem] overflow-hidden`} style={{ aspectRatio: '3/4' }}>
                  <img
                    src="/images/Profile.png"
                    alt="Jai Choraria"
                    className="w-full h-full object-cover block"
                  />
                </div>
              </div>
            </div>

            {/* Right - Bio */}
            <div className="flex flex-col justify-start pt-8">
              <h1 className={`text-6xl leading-tight mb-8 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
                Hi!, I'm Jai 
              </h1>

              <div className={`space-y-6 ${isDarkMode ? 'text-gray-300' : 'text-stone-600'} text-lg`} style={{ fontFamily: 'Georgia, serif' }}>
                <p className="leading-relaxed">
                  I'm a biomedical engineer passionate about bridging the gap between 
                  <span className="relative inline-block mx-1">
                    <span className={`relative z-10 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}> cutting-edge technology </span>
                    <span className={`absolute bottom-1 left-0 right-0 h-3 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-200'} -z-10`}></span>
                  </span>
                  and healthcare innovation.
                </p>

                <p className="leading-relaxed">
                  I have worked in fields focussing on developing AI-powered solutions for medical diagnostics, RNA therapeutics, 
                  cancer research, while my education has introduced me to design and innovate medical devices. I believe that with  
                  these skills, Iwould be able to play a significant role in transforming patient care and accelerating scientific discovery.
                </p>

                <p className="leading-relaxed">
                  With expertise spanning bioinformatics, deep learning, and medical imaging, I've contributed 
                  to projects that have improved drug delivery systems, automated diagnostic workflows, and 
                  advanced our understanding of complex biological systems.
                </p>

                <p className="leading-relaxed">
                  When I'm not coding or analyzing data, you'll find me exploring new cuisines, reading 
                  research papers, watching F1 or playing Squash. Always eager to collaborate on 
                  challenging problems that can create impact.
                </p>
              </div>

              {/* Download Resume Button */}
              <div className="mt-8">
                <a
                  href="/assets/Jai_Choraria_resume.pdf"
                  download
                  aria-label="Download Resume PDF"
                  className={`inline-flex items-center gap-2 px-6 py-3 ${isDarkMode ? 'bg-white text-gray-900 hover:bg-gray-200' : 'bg-stone-900 text-stone-50 hover:bg-stone-700'} rounded-full transition-colors`}
                  style={{ fontFamily: 'Georgia, serif' }}
                  rel="noopener noreferrer"
                >
                  <FaDownload className="w-5 h-5" />
                  <span>Download Resume</span>
                </a>
              </div>
            </div>
          </div>

          {/* Skills Section */}
          <div className="mb-32">
            <h2 className={`text-5xl mb-12 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
              Skills & Expertise
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {skills.map((skillGroup, idx) => (
                <div key={idx} className="space-y-4">
                  <h3 className={`text-2xl mb-4 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
                    {skillGroup.category}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skillGroup.items.map((skill, skillIdx) => (
                      <span 
                        key={skillIdx}
                        className={`px-4 py-2 ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'} rounded-full text-sm transition-colors`}
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Interests Section */}
          <div className="mb-32">
            <h2 className={`text-5xl mb-12 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
              Beyond the Code
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Link 
                to="/research-papers"
                className={`p-8 ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-stone-100 hover:bg-stone-200'} rounded-2xl transition-colors cursor-pointer`}
              >
                <FaBook className={`w-10 h-10 ${isDarkMode ? 'text-gray-400' : 'text-stone-700'} mb-4`} />
                <h3 className={`text-xl mb-3 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Research Papers
                </h3>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-stone-600'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Some recommendations and summaries of interesting research papers in computational biology, AI, and biomedical engineering.
                </p>
              </Link>

              <Link 
                to="/open-source"
                className={`p-8 ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-stone-100 hover:bg-stone-200'} rounded-2xl transition-colors cursor-pointer`}
              >
                <FaLaptopCode className={`w-10 h-10 ${isDarkMode ? 'text-gray-400' : 'text-stone-700'} mb-4`} />
                <h3 className={`text-xl mb-3 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Open Source
                </h3>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-stone-600'}`} style={{ fontFamily: 'Georgia, serif' }}>
                  Contributing to bioinformatics tools and sharing knowledge with the community.
                </p>
              </Link>
            </div>
          </div>

          {/* CTA Section */}
          <div className={`text-center py-20 px-8 ${isDarkMode ? 'bg-gray-800' : 'bg-stone-100'} rounded-3xl`}>
            <h2 className={`text-4xl mb-6 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'Georgia, serif' }}>
              Let's Work Together
            </h2>
            <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-stone-600'} mb-8 max-w-2xl mx-auto`} style={{ fontFamily: 'Georgia, serif' }}>
              I'm always open to discussing new projects, research collaborations, or opportunities in computational biology and medical AI.
            </p>
            <a 
              href="mailto:jchoraria17@gmail.com"
              className={`inline-block px-8 py-4 ${isDarkMode ? 'bg-white text-gray-900 hover:bg-gray-200' : 'bg-stone-900 text-stone-50 hover:bg-stone-700'} rounded-full text-lg transition-colors`} 
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Get In Touch
            </a>
          </div>

        </div>
      </main>
    </div>
  );
};

export default About;